import type { Draft } from 'immer';
import type { GameEvent } from './events';
import type { CardTitle, PlayerId } from './ids';
import { aliveCount, type GameState } from './state';

/**
 * 回合机制：谁行动、还要行动几次、死亡、胜负。
 *
 * 这些都在 `Draft<GameState>` 上就地改，由 `reducer.ts` 统一包在 immer 里。
 *
 * ## 攻击叠加为什么不能只用一个计数器
 *
 * 官方例子是 **2 → 4 → 6**（`docs/rules.md` §5.5）：
 * 被攻击 2 回合的人打出一张 Signal，下家吃 **4** 回合。
 *
 * 但普通回合的 `turnsRemaining` 也是 1，直接套「剩余 + 2」会得到 3（错，应该 2）。
 * 所以 `TurnSlot` 额外用 `fromAttack` 区分「我自己的普通回合」和「被攻击欠的债」：
 *
 * ```
 * 下一家的回合数 = (本家 fromAttack ? 本家 turnsRemaining : 0) + 2
 * ```
 */

/** 从某个座位往后找下一个存活玩家的座位。找不到（全死）返回 -1 */
export function nextAliveSeat(draft: Draft<GameState>, fromSeat: number): number {
  const n = draft.players.length;
  if (n === 0) return -1;
  for (let step = 1; step <= n; step++) {
    const seat = (fromSeat + step) % n;
    if (draft.players[seat]?.alive === true) return seat;
  }
  return -1;
}

function seatOf(draft: Draft<GameState>, player: PlayerId): number {
  return draft.players.findIndex((p) => p.id === player);
}

/** 把回合交给下一个存活玩家（默认：普通回合，1 次行动） */
export function passToNext(
  draft: Draft<GameState>,
  events: GameEvent[],
  override?: { turnsRemaining: number; fromAttack: boolean; attackSource?: CardTitle | null },
): void {
  const current = draft.turn;
  if (current === null) return;

  events.push({ t: 'turn/ended', player: current.player });

  const fromSeat = seatOf(draft, current.player);
  const seat = nextAliveSeat(draft, fromSeat);
  if (seat < 0) {
    // 没有下一个存活玩家了 —— 交给胜利判定处理
    draft.turn = null;
    return;
  }

  const next = draft.players[seat];
  if (next === undefined) return;

  const slot: Draft<import('./state').TurnSlot> = {
    player: next.id,
    turnsRemaining: override?.turnsRemaining ?? 1,
    fromAttack: override?.fromAttack ?? false,
    attackSource: override?.attackSource ?? null,
  };
  draft.turn = slot;
  events.push({ t: 'turn/started', player: next.id, turnsRemaining: slot.turnsRemaining });
}

/**
 * 消耗掉「这一次行动」，回合结束。
 *
 * - 还有剩余回合（被攻击欠着）→ 还是同一个人行动
 * - 剩 0 次 → 交给下家
 */
export function consumeTurn(draft: Draft<GameState>, events: GameEvent[]): void {
  const current = draft.turn;
  if (current === null) return;

  // ⚠️ 当前玩家已经出局 → **无论还欠几个回合都直接交棒**。
  //
  // 漏掉这一条会**把整局卡死**：被 Signal 攻击欠着 2 回合的人，在第 1 个回合
  // 抽到致命牌又没拆弹，就出局了；此时 `turnsRemaining` 还剩 1，
  // 于是回合留在死人手上 —— 而他既不能出牌也不能抽牌，谁都动不了。
  //
  // （这条是 `npm run sim` 撞出来的：机器人报"合法动作 0 个"，但牌堆还有 14 张。）
  const holder = draft.players.find((p) => p.id === current.player);
  if (holder === undefined || !holder.alive) {
    passToNext(draft, events);
    return;
  }

  const left = current.turnsRemaining - 1;
  if (left > 0) {
    current.turnsRemaining = left;
    return;
  }
  passToNext(draft, events);
}

/** 把某个玩家的一整手牌丢进弃牌堆，并标记出局（`docs/rules.md` §9.2） */
export function killPlayer(
  draft: Draft<GameState>,
  player: PlayerId,
  events: GameEvent[],
): void {
  const target = draft.players.find((p) => p.id === player);
  if (target === undefined || !target.alive) return;

  const hand = [...target.hand];
  target.hand = [];
  target.alive = false;
  draft.discardPile.push(...hand);

  if (hand.length > 0) events.push({ t: 'card/discarded', cards: hand });

  // 出局的人不再持有任何已知信息
  draft.knowledge[player] = { knownTop: [], knownInDraw: [], knownInHand: [] };

  checkWinner(draft, events);
}

/** 只剩一个存活玩家就结束（`docs/rules.md` §7） */
export function checkWinner(draft: Draft<GameState>, events: GameEvent[]): void {
  if (draft.phase !== 'playing') return;

  const alive = aliveCount(draft);
  if (alive > 1) return;

  draft.phase = 'finished';
  draft.turn = null;
  draft.resolution = null;
  draft.decision = null;

  const survivor = draft.players.find((p) => p.alive);
  draft.winner = survivor?.id ?? null;
  if (survivor !== undefined) {
    events.push({ t: 'game/finished', winner: survivor.id });
  }
}

/** 把「我手上这张牌」移到弃牌堆 */
export function discardFromHand(
  draft: Draft<GameState>,
  player: PlayerId,
  cards: readonly import('./ids').CardUid[],
): boolean {
  const target = draft.players.find((p) => p.id === player);
  if (target === undefined) return false;

  const removing = new Set(cards);
  const kept = target.hand.filter((uid) => !removing.has(uid));
  if (kept.length === target.hand.length) return false;

  target.hand = kept;
  draft.discardPile.push(...cards);
  return true;
}
